import { Heart, Target, Award, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Inclusivity",
      description:
        "We celebrate diversity and create a safe, welcoming space for everyone. All members feel respected and valued, regardless of their background or fitness level.",
    },
    {
      icon: Target,
      title: "Personalization",
      description:
        "No cookie-cutter programs here. Every training plan is customized to your unique goals, fitness level, and lifestyle for maximum results.",
    },
    {
      icon: Users,
      title: "Community",
      description:
        "Train alongside supportive, like-minded individuals who celebrate your wins and motivate you through challenges. Together, we're stronger.",
    },
    {
      icon: Award,
      title: "Excellence",
      description:
        "Our certified trainers bring expertise, passion, and dedication to every session. We're committed to helping you achieve and exceed your goals.",
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxneW0lMjBmaXRuZXNzfGVufDB8fHx8MTc3MjAwMzE2NXww&ixlib=rb-4.1.0&q=85"
            alt="GetFit Studio"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About GetFit Fitness Studio</h1>
            <p className="text-xl text-gray-300">
              More than a gym - we're a community committed to your transformation
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  GetFit Fitness Studio was born from a simple belief: everyone deserves a fitness
                  space where they feel welcome, supported, and empowered to reach their full
                  potential.
                </p>
                <p>
                  Located in the heart of Malmesbury, we've built more than just a gym - we've
                  created a community. A place where beginners feel comfortable taking their first
                  steps, where experienced athletes push new boundaries, and where everyone
                  celebrates each other's victories.
                </p>
                <p>
                  Our boutique studio offers the personal attention of one-on-one training with the
                  energy and motivation of a supportive community. We're proud to be an inclusive
                  space that welcomes people of all backgrounds, fitness levels,
                  and goals.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7991623/pexels-photo-7991623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Training Session"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-r from-[#9FFF00] to-[#8AE600] text-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Mission</h2>
            <p className="text-2xl leading-relaxed font-medium">
              To empower individuals to transform their lives through personalized fitness training,
              unwavering accountability, and a supportive community that celebrates diversity and
              champions every milestone.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="border-2 hover:border-[#9FFF00] transition-all hover:shadow-xl group"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#9FFF00] to-[#8AE600] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              What Makes Us Different
            </h2>

            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-[#9FFF00] text-black rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Boutique Studio Experience
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Unlike crowded big-box gyms, our intimate studio environment ensures you get
                    personalized attention every session. You're not just another member - you're
                    part of our fitness family.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 bg-[#9FFF00] text-black rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Certified Personal Trainers
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Our trainers aren't just knowledgeable - they're passionate about your success.
                    With years of experience and ongoing education, they create programs that work
                    and keep you motivated.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 bg-[#9FFF00] text-black rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Results-Driven Approach
                  </h3>
                  <p className="text-gray-600 text-lg">
                    We track your progress, adjust your program as needed, and hold you accountable
                    to your goals. Our proven methods deliver real, sustainable transformations.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 bg-[#9FFF00] text-black rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Truly Inclusive Environment
                  </h3>
                  <p className="text-gray-600 text-lg">
                    We walk the walk when it comes to inclusivity. Our welcoming space
                    actively celebrates diversity, and our community genuinely supports each other
                    regardless of background, identity, or fitness level. Everyone belongs at GetFit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
